import React, { useEffect, useMemo, useRef, useState } from 'react';

const typeConfig = {
  narrow: { fov: 30, radius: 100, color: '#ffffff' },
  medium: { fov: 60, radius: 120, color: '#ffffff' },
  wide: { fov: 120, radius: 140, color: '#ffffff' },
};

const genId = () => `cone-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const getClientPoint = (evt) => {
  if (evt?.touches?.length) {
    return {
      clientX: evt.touches[0].clientX,
      clientY: evt.touches[0].clientY,
    };
  }
  if (evt?.changedTouches?.length) {
    return {
      clientX: evt.changedTouches[0].clientX,
      clientY: evt.changedTouches[0].clientY,
    };
  }
  if (typeof evt?.clientX === 'number' && typeof evt?.clientY === 'number') {
    return { clientX: evt.clientX, clientY: evt.clientY };
  }
  return null;
};

const toSvgPoint = (evt, svgRef) => {
  const svg = svgRef.current;
  if (!svg) return { x: 0, y: 0 };
  const rect = svg.getBoundingClientRect();
  const point = getClientPoint(evt);
  if (!point) return { x: 0, y: 0 };
  const x = ((point.clientX - rect.left) / rect.width) * 1000;
  const y = ((point.clientY - rect.top) / rect.height) * 600;
  return { x, y };
};

const buildArcPath = (origin, target, fovDeg, radius) => {
  const angle = Math.atan2(target.y - origin.y, target.x - origin.x);
  const half = (fovDeg * Math.PI) / 180 / 2;
  const start = angle - half;
  const end = angle + half;
  const steps = Math.max(8, Math.floor(fovDeg / 5));

  const points = [];
  points.push(`M ${origin.x} ${origin.y}`);
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const a = start + (end - start) * t;
    const px = origin.x + Math.cos(a) * radius;
    const py = origin.y + Math.sin(a) * radius;
    points.push(`${i === 0 ? 'L' : ''} ${px} ${py}`);
  }
  points.push('Z');
  return points.join(' ');
};

const clampToRadius = (origin, point, radius) => {
  const dx = point.x - origin.x;
  const dy = point.y - origin.y;
  const dist = Math.sqrt(dx * dx + dy * dy);
  if (dist === 0) return { x: origin.x + radius, y: origin.y };
  if (dist <= radius) return point;
  const scale = radius / dist;
  return { x: origin.x + dx * scale, y: origin.y + dy * scale };
};

const VisionCones = ({
  loadedCones = [],
  coneType = 'medium',
  onConesChange = () => {},
}) => {
  const svgRef = useRef(null);
  const [cones, setCones] = useState([]);
  const [draftCone, setDraftCone] = useState(null);
  const draggingRef = useRef(null); // { id, mode: 'origin' | 'direction', delta?: {x,y} }
  const moveListenerRef = useRef(null);
  const upListenerRef = useRef(null);

  useEffect(() => {
    if (Array.isArray(loadedCones)) {
      setCones(loadedCones);
    } else {
      setCones([]);
    }
  }, [loadedCones]);

  const typeSettings = useMemo(() => typeConfig[coneType] || typeConfig.medium, [coneType]);

  const commitCones = (next) => {
    setCones(next);
    onConesChange(next);
  };

  const handlePointerMove = (e) => {
    if (!draggingRef.current) return;
    if (e.cancelable) e.preventDefault();
    const { id, mode, delta } = draggingRef.current;
    const point = toSvgPoint(e, svgRef);
    commitCones(
      cones.map((cone) => {
        if (cone.id !== id) return cone;
        if (mode === 'origin') {
          const nextOrigin = point;
          const nextTarget = delta
            ? { x: nextOrigin.x + delta.x, y: nextOrigin.y + delta.y }
            : cone.target;
          return { ...cone, origin: nextOrigin, target: nextTarget };
        }
        if (mode === 'direction') {
          const clampedTarget = clampToRadius(cone.origin, point, cone.radius);
          return { ...cone, target: clampedTarget };
        }
        return cone;
      })
    );
  };

  const handlePointerUp = () => {
    draggingRef.current = null;
    if (moveListenerRef.current) {
      window.removeEventListener('mousemove', moveListenerRef.current);
      window.removeEventListener('touchmove', moveListenerRef.current);
      moveListenerRef.current = null;
    }
    if (upListenerRef.current) {
      window.removeEventListener('mouseup', upListenerRef.current);
      window.removeEventListener('touchend', upListenerRef.current);
      window.removeEventListener('touchcancel', upListenerRef.current);
      upListenerRef.current = null;
    }
  };

  const startHandleDrag = (e, cone, mode) => {
    e.stopPropagation();
    const isTouchEvent = !!(e?.touches || e?.changedTouches);
    if (!isTouchEvent) {
      e.preventDefault();
    }
    const delta = {
      x: cone.target.x - cone.origin.x,
      y: cone.target.y - cone.origin.y,
    };
    draggingRef.current = { id: cone.id, mode, delta };
    if (!moveListenerRef.current) {
      moveListenerRef.current = (evt) => handlePointerMove(evt);
      window.addEventListener('mousemove', moveListenerRef.current);
      window.addEventListener('touchmove', moveListenerRef.current, { passive: false });
    }
    if (!upListenerRef.current) {
      upListenerRef.current = () => handlePointerUp();
      window.addEventListener('mouseup', upListenerRef.current);
      window.addEventListener('touchend', upListenerRef.current);
      window.addEventListener('touchcancel', upListenerRef.current);
    }
  };

  const handlePoint = (cone) => {
    const dx = cone.target.x - cone.origin.x;
    const dy = cone.target.y - cone.origin.y;
    const scale = 0.7;
    return { x: cone.origin.x + dx * scale, y: cone.origin.y + dy * scale };
  };

  const handleSize = (cone) => {
    const size = cone.radius * 0.08;
    return Math.max(6, Math.min(12, size));
  };

  useEffect(() => {
    return () => {
      if (moveListenerRef.current) {
        window.removeEventListener('mousemove', moveListenerRef.current);
        window.removeEventListener('touchmove', moveListenerRef.current);
      }
      if (upListenerRef.current) {
        window.removeEventListener('mouseup', upListenerRef.current);
        window.removeEventListener('touchend', upListenerRef.current);
        window.removeEventListener('touchcancel', upListenerRef.current);
      }
    };
  }, []);

  return (
    <svg
      ref={svgRef}
      viewBox="0 0 1000 600"
      style={{ width: '100%', height: '100%', pointerEvents: 'none' }}
      pointerEvents="none"
    >
      {cones.map((cone) => (
        <g key={cone.id}>
          <path
            d={buildArcPath(cone.origin, cone.target, cone.fov, cone.radius)}
            fill={cone.color}
            fillOpacity={0.08}
            stroke="none"
            pointerEvents="none"
          />
          <circle
            cx={cone.origin.x}
            cy={cone.origin.y}
            r={6}
            fill="#fff"
            stroke={cone.color}
            strokeWidth={2}
            onMouseDown={(evt) => startHandleDrag(evt, cone, 'origin')}
            onTouchStart={(evt) => startHandleDrag(evt, cone, 'origin')}
            style={{ cursor: 'grab', pointerEvents: 'all', touchAction: 'none' }}
          />
          {(() => {
            const hp = handlePoint(cone);
            const sz = handleSize(cone);
            return (
              <rect
                x={hp.x - sz / 2}
                y={hp.y - sz / 2}
                width={sz}
                height={sz}
                fill="#ff4d4d"
                stroke="#ff4d4d"
                strokeWidth={2}
                transform={`rotate(45 ${hp.x} ${hp.y})`}
                onMouseDown={(evt) => startHandleDrag(evt, cone, 'direction')}
                onTouchStart={(evt) => startHandleDrag(evt, cone, 'direction')}
                style={{ cursor: 'grab', pointerEvents: 'all', touchAction: 'none' }}
              />
            );
          })()}
        </g>
      ))}
    </svg>
  );
};

export default VisionCones;
